"use client";

import React, { useEffect, useRef, useState } from "react";

interface Feature {
  id: string;
  tag: string;
  tagColor: string;
  title: string;
  subtitle: string;
  description: string;
  bullets: string[];
  icon: React.ReactNode;
  visual: React.ReactNode;
  span?: "wide" | "normal";
}

// ─── Visual: Airlock Calendar ───
const AirlockVisual: React.FC = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
    <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <span className="text-xs font-semibold" style={{ color: "#F59E0B", letterSpacing: "0.1em" }}>MARCH 2026</span>
      <div className="flex gap-1">
        {["M","T","W","T","F"].map((d, i) => (
          <div key={i} className="w-8 h-5 flex items-center justify-center text-xs" style={{ color: "#5C5A56" }}>{d}</div>
        ))}
      </div>
    </div>
    <div className="p-4 space-y-2">
      {[
        { time: "9:00", label: "Deep Work", locked: true, color: "#F59E0B" },
        { time: "10:30", label: "Sarah — Atlas call", locked: false, color: "#22C55E" },
        { time: "12:00", label: "Focus Block", locked: true, color: "#F59E0B" },
        { time: "2:00", label: "Board prep", locked: false, color: "#818CF8" },
        { time: "4:00", label: "Protected", locked: true, color: "#F59E0B" },
      ].map((slot, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-3 py-2 rounded-lg"
          style={{ background: slot.locked ? `${slot.color}12` : "rgba(255,255,255,0.02)", border: `1px solid ${slot.locked ? `${slot.color}30` : "rgba(255,255,255,0.04)"}` }}
        >
          <span className="text-xs w-10 shrink-0" style={{ color: "#5C5A56" }}>{slot.time}</span>
          <span className="text-xs flex-1" style={{ color: slot.locked ? slot.color : "#9B9891" }}>{slot.label}</span>
          {slot.locked && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke={slot.color} strokeWidth="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke={slot.color} strokeWidth="2"/>
            </svg>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ─── Visual: Ghostwriter Memory ───
const GhostwriterVisual: React.FC = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const contexts = [
    { person: "Rachel Torres", role: "CFO, Meridian Capital", tone: "Formal", memory: "Prefers bullet summaries, allergic to small talk, last discussed Series B terms in Nov.", draft: "Rachel — Per our Nov discussion, here's the updated cap table. Three items require your sign-off before EOW." },
    { person: "Dev Anand", role: "CTO, Axion Labs", tone: "Technical", memory: "Responds well to data, prefers async. Mentioned latency concerns in last 3 threads.", draft: "Dev — Ran the benchmarks you requested. P99 latency is down 23ms. Full report attached — worth a sync?" },
  ];
  const current = contexts[activeIdx];

  return (
    <div className="w-full space-y-3">
      <div className="flex gap-2">
        {contexts.map((c, i) => (
          <button
            key={i}
            onClick={() => setActiveIdx(i)}
            className="flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200"
            style={{
              background: i === activeIdx ? "rgba(129,140,248,0.15)" : "rgba(255,255,255,0.03)",
              border: `1px solid ${i === activeIdx ? "rgba(129,140,248,0.3)" : "rgba(255,255,255,0.06)"}`,
              color: i === activeIdx ? "#818CF8" : "#5C5A56",
            }}
          >
            {c.person.split(" ")[0]}
          </button>
        ))}
      </div>
      <div className="rounded-xl p-3" style={{ background: "rgba(129,140,248,0.06)", border: "1px solid rgba(129,140,248,0.12)" }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: "rgba(129,140,248,0.2)", color: "#818CF8" }}>
            {current.person[0]}
          </div>
          <div>
            <div className="text-xs font-semibold" style={{ color: "#F0EDE8" }}>{current.person}</div>
            <div className="text-xs" style={{ color: "#5C5A56", fontSize: "10px" }}>{current.role}</div>
          </div>
          <span className="ml-auto text-xs px-2 py-0.5 rounded" style={{ background: "rgba(129,140,248,0.15)", color: "#818CF8", fontSize: "10px" }}>Tone: {current.tone}</span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: "#9B9891" }}>
          <span className="font-medium" style={{ color: "#818CF8" }}>Memory: </span>
          {current.memory}
        </p>
      </div>
      <div className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-1.5 mb-2">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#22C55E", animation: "dot-blink 2s ease-in-out infinite" }} />
          <span className="text-xs font-semibold" style={{ color: "#22C55E", fontSize: "10px" }}>DRAFT READY</span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: "#9B9891" }}>{current.draft}</p>
      </div>
    </div>
  );
};

// ─── Visual: Elevate Brief ───
const ElevateBriefVisual: React.FC = () => (
  <div className="w-full space-y-2">
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="text-base font-semibold" style={{ color: "#F0EDE8" }}>Monday, Mar 2</div>
        <div className="text-xs" style={{ color: "#5C5A56" }}>Your 6 AM brief is ready</div>
      </div>
      <div className="text-right">
        <div className="text-xs font-semibold" style={{ color: "#F59E0B" }}>Stress Index</div>
        <div className="flex items-center gap-1.5 mt-1">
          {[1,2,3,4,5].map((i) => (
            <div key={i} className="w-4 h-1.5 rounded-full" style={{
              background: i <= 2 ? "#22C55E" : i === 3 ? "#F59E0B" : "rgba(255,255,255,0.08)"
            }} />
          ))}
        </div>
      </div>
    </div>
    {[
      { icon: "📧", label: "Inbox", value: "47 new · 8 need you", color: "#F59E0B" },
      { icon: "📅", label: "Calendar", value: "4 meetings · 1 conflict resolved", color: "#818CF8" },
      { icon: "✍️", label: "Ghostwriter", value: "6 replies drafted & ready", color: "#22C55E" },
      { icon: "🔒", label: "Airlock", value: "Focus blocks protected until 11 AM", color: "#F59E0B" },
    ].map((item, i) => (
      <div
        key={i}
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
      >
        <span className="text-base">{item.icon}</span>
        <div className="flex-1">
          <div className="text-xs font-medium" style={{ color: "#5C5A56" }}>{item.label}</div>
          <div className="text-xs" style={{ color: "#9B9891" }}>{item.value}</div>
        </div>
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: item.color }} />
      </div>
    ))}
  </div>
);

const features: Feature[] = [
  {
    id: "airlock",
    tag: "Airlock Engine",
    tagColor: "#F59E0B",
    title: "Zero-click meeting booking",
    subtitle: "Your calendar, under new management.",
    description: "The Airlock Scheduling Engine reads your energy patterns, workload, and priorities — then automatically protects focus time and books meetings without a single tap from you.",
    bullets: ["Blocks deep work windows automatically", "Routes meeting requests through AI negotiation", "Syncs with Gmail & Outlook calendars in real-time"],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M8 2v3M16 2v3M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    visual: <AirlockVisual />,
    span: "wide",
  },
  {
    id: "ghostwriter",
    tag: "Ghostwriter AI",
    tagColor: "#818CF8",
    title: "Replies that sound like you — always",
    subtitle: "Long-Term Relationship Memory.",
    description: "Ghostwriter learns every contact's preferences, your tone history, and relationship context — then drafts replies indistinguishable from your own writing.",
    bullets: ["Learns from 6+ months of your email history", "Per-contact tone calibration", "One-tap approve & send"],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="#818CF8" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    visual: <GhostwriterVisual />,
  },
  {
    id: "brief",
    tag: "Elevate Brief",
    tagColor: "#22C55E",
    title: "Your 6 AM command center",
    subtitle: "Before the world wakes up.",
    description: "Every morning at 6 AM, Elevate Brief synthesizes your inbox, calendar, and priorities into a single ambient dashboard — with all decisions pre-processed.",
    bullets: ["Ambient stress heatmap for your week", "Priority triage — what needs you vs. what doesn't", "Drafted replies waiting for approval"],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="4" stroke="#22C55E" strokeWidth="1.8"/>
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="#22C55E" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    visual: <ElevateBriefVisual />,
  },
];

const FeaturesSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="features"
      ref={sectionRef}
      className="relative py-32 px-6 overflow-hidden"
      style={{ background: "#0D0F14" }}
    >
      {/* Background accent */}
      <div
        className="absolute right-0 top-1/2 pointer-events-none"
        style={{
          width: "500px", height: "500px",
          background: "radial-gradient(ellipse at center, rgba(245,158,11,0.06) 0%, transparent 70%)",
          transform: "translate(30%, -50%)",
          filter: "blur(60px)",
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <div
          className="text-center mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-xs font-semibold tracking-widest uppercase"
            style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.15)", color: "#F59E0B" }}
          >
            Triple-Threat Autonomy
          </div>
          <h2
            className="font-display text-center leading-tight mb-4"
            style={{ fontSize: "clamp(36px, 5vw, 60px)", color: "#F0EDE8", letterSpacing: "-0.02em" }}
          >
            Three engines.{" "}
            <span className="font-display italic" style={{ color: "#9B9891" }}>One mind.</span>
          </h2>
          <p className="max-w-xl mx-auto" style={{ color: "#9B9891", fontSize: "18px", lineHeight: "1.6" }}>
            Each module works independently or as a unified system — adapting to your workflow from day one.
          </p>
        </div>

        {/* Feature cards bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {features.map((feature, idx) => (
            <div
              key={feature.id}
              className={`group rounded-3xl p-8 transition-all duration-500 cursor-default ${feature.span === "wide" ? "lg:col-span-2" : ""}`}
              style={{
                background: "linear-gradient(145deg, #1A1D26 0%, #13151C 100%)",
                border: "1px solid rgba(255,255,255,0.06)",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(32px)",
                transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${0.15 + idx * 0.1}s`,
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = `${feature.tagColor}30`;
                el.style.boxShadow = `0 0 40px ${feature.tagColor}10`;
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = "rgba(255,255,255,0.06)";
                el.style.boxShadow = "none";
              }}
            >
              <div className={`flex flex-col ${feature.span === "wide" ? "lg:flex-row" : ""} gap-8`}>
                {/* Left: Content */}
                <div className={`flex flex-col ${feature.span === "wide" ? "lg:w-2/5" : "w-full"}`}>
                  {/* Tag + Icon */}
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${feature.tagColor}15` }}
                    >
                      {feature.icon}
                    </div>
                    <span
                      className="text-xs font-bold uppercase tracking-widest"
                      style={{ color: feature.tagColor }}
                    >
                      {feature.tag}
                    </span>
                  </div>

                  <h3
                    className="font-semibold mb-2 leading-snug"
                    style={{ fontSize: "22px", color: "#F0EDE8", letterSpacing: "-0.01em" }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-sm font-medium mb-3" style={{ color: feature.tagColor }}>
                    {feature.subtitle}
                  </p>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: "#9B9891" }}>
                    {feature.description}
                  </p>

                  <ul className="space-y-2 mt-auto">
                    {feature.bullets.map((bullet, bi) => (
                      <li key={bi} className="flex items-start gap-2.5 text-sm" style={{ color: "#9B9891" }}>
                        <div
                          className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                          style={{ background: feature.tagColor }}
                        />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right: Visual */}
                <div className={`${feature.span === "wide" ? "lg:w-3/5" : "w-full"}`}>
                  {feature.visual}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;